import { openProfileDocument } from "../../../backend/transport";
import React, { useEffect, useRef, useState } from 'react';
import { DetailBox, PaginatedTable, TitleBar, SearchBar } from '../../common';
import {
  TITLES,
  ROUTES,
  FABRIC_DISPATCH_INNER_COLUMN,
  BUTTON,
  POPUP_TYPE,
  POPUP_MSG,
  LOCAL_STORAGE,
} from '../../../constants';
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../../backend/navigation";
import {
  dispatchDetailsBox,
  fabricDispatchProductCompiler,
} from '../../../utils/dataCompiler/dispatchDataCompiler';
import { useLoader } from '../../../context/LoaderContext';
import { useDebounce } from '../../../hooks';
import { updateReceiptQty, vendorReceipt, vendorReceiptMoveIds } from '../../../service/receipt';
import { dispatchBillCreation, vendorBill } from '../../../service';
import { getLocalStorageItem, popup } from '../../../utils';
import { Button, PopUpBox, ThresholdWarningPopup } from '../../shared';
import {
  markDispatched,
  printPackingListDispatch,
  vendorUpdateDispatchDetail,
} from '../../../service';

export const FabricDispatchDetail = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const { state } = useLocation();
  const [popUp, setPopUp] = useState(0);
  const [popUpData, setPopUpData] = useState('');
  const [productData, setProductData] = useState([]);
  const [reload] = useState(false); // Keep reload for useEffect dependency
  const [dispatchData, setDispatchData] = useState();
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoveIdsLoading, setIsMoveIdsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState(false);
  const [isUpdateDetailsLoading, setIsUpdateDetailsLoading] = useState(false);
  const [editValues, setEditValues] = useState({
    invoice_no: dispatchData?.invoice_no || '',
    container_no: dispatchData?.physical_container_no || '',
  });
  const [activeUpdateRequests, setActiveUpdateRequests] = useState(0);
  const [isValidateLoading, setIsValidateLoading] = useState(false);
  const [showThresholdWarning, setShowThresholdWarning] = useState(false);
  const [thresholdWarningData, setThresholdWarningData] = useState(null);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const isUpdateLoading = activeUpdateRequests > 0;
  const [showCheckBill, setShowCheckBill] = useState(false);
  const [isCheckBillLoading, setIsCheckBillLoading] = useState(false);
  const [showCreateBill, setShowCreateBill] = useState(false);
  const [isCreateBillLoading, setIsCreateBillLoading] = useState(false);

  useEffect(() => {
    if (dispatchData) {
      setEditValues({
        invoice_no: dispatchData?.invoice_no || '',
        container_no: dispatchData?.physical_container_no || '',
      });
    }
  }, [dispatchData]);

  let filteredCol =
    getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) !== 'fabric'
      ? FABRIC_DISPATCH_INNER_COLUMN.filter(
        (col) => col.name !== 'Rolls' && col.name !== 'Color' && col.name !== 'Size',
      )
      : FABRIC_DISPATCH_INNER_COLUMN;

  let filteredColumn =
    dispatchData?.dispatch_status === 'dispatched' || dispatchData?.state === 'done' || dispatchData?.state === 'cancel'
      ? filteredCol.filter((col) => col.name !== 'Action')
      : filteredCol;

  const nilMoveLineCheck = (productData) => {
    if (productData?.length) {
      const allLinesWithZeroQty = productData.every((move) => {
        const doneQty = move?.producing_quantity || move?.quantity || 0;
        return doneQty === 0;
      });
      return allLinesWithZeroQty;
    }
    return false;
  };

  // Fetch receipt header data (without move_ids)
  const getReceiptData = useDebounce(() => {
    setIsLoading(true);
    vendorReceipt({
      data: { receipt_ids: state?.id, page_number: 1 },
    })
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.records?.length > 0) {
          setDispatchData(response?.data?.records?.[0]);
          if (response.data.records[0].dispatch_bill_id) {
            setShowCheckBill(response.data.records[0]?.dispatch_bill_id?.id);
          } else if (response.data.records[0].dispatch_status === 'dispatched' || response.data.records[0].dispatch_status === 'received') {
            setShowCreateBill(true);
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        popup('error', error.message);
      });
  }, 400);

  // Fetch move_ids data with search
  const getProductsList = useDebounce((additionalParams = {}) => {
    if (!state?.id || String(dispatchData?.id) !== String(state.id)) return;
    
    setIsMoveIdsLoading(true);
    
    const params = {
      receipt_id: state.id,
      page_number: additionalParams.page_number || pageNumber,
      search: searchTerm
        ? {
            future_lot: searchTerm,
            'product_id.default_code': searchTerm,
            'product_id.name': searchTerm,
            'move_line_ids.lot_name': searchTerm,
          }
        : {},
      ...additionalParams,
    };

    vendorReceiptMoveIds({ data: params })
      .then((response) => {
        setIsMoveIdsLoading(false);
        if (response?.data?.records?.length > 0) {
          // Compile move_ids data 
          const compiledData = fabricDispatchProductCompiler({
            move_ids: response?.data?.records,
            vendor_shipment: dispatchData?.vendor_shipment
          });
          setProductData(compiledData);
          setPaginationData(response.data.params);
        } else {
          setProductData([]);
          setPaginationData({ page: 1, start: 0, end: 0, total_records: 0 });
        }
        setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
      })
      .catch((error) => {
        setIsMoveIdsLoading(false);
        setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
      });
  }, 400);

  useEffect(() => {
    if (!state) navigate(ROUTES.FABRIC_DISPATCH_DETAIL);
    if (state?.id) {
      getReceiptData();
    }
  }, [reload, state, navigate, getReceiptData]);

  useEffect(() => {
    if (state?.id && String(dispatchData?.id) === String(state.id)) {
      getProductsList();
    }
  }, [dispatchData, state?.id, getProductsList]);

  // Handle search - trigger API call when search button is clicked
  const handleSearch = (submitted) => {
    setSearch(submitted);
    if (submitted && state?.id) {
      setPageNumber(1);
      // Call API directly for immediate search
      setIsMoveIdsLoading(true);
      const params = {
        receipt_id: state.id,
        page_number: 1,
        search: searchTerm.trim()
          ? {
              future_lot: searchTerm.trim(),
              'product_id.default_code': searchTerm.trim(),
              'product_id.name': searchTerm.trim(),
              'move_line_ids.lot_name': searchTerm.trim(),
            }
          : {},
      };
      vendorReceiptMoveIds({ data: params })
        .then((response) => {
          setIsMoveIdsLoading(false);
          if (response?.data?.records?.length > 0) {
            const compiledData = fabricDispatchProductCompiler({
              move_ids: response.data.records,
              vendor_shipment: dispatchData?.vendor_shipment
            });
            setProductData(compiledData);
            setPaginationData(response.data.params);
          } else {
            setProductData([]);
            setPaginationData({ page: 1, start: 0, end: 0, total_records: 0 });
          }
        })
        .catch((error) => {
          setIsMoveIdsLoading(false);
          popup('error', error.message);
        });
    }
  };

  const handlePageChange = (value) => {
    const page = typeof value === 'function' ? value(pageNumber) : value;
    if (!page || page === pageNumber) return;
    setPageNumber(page);
    if (state?.id) getProductsList({ page_number: page });
  };

  const updateReceipt = async (data, force_submit = false) => {
    // dispatch(setLoaderVisibility(true));
    setActiveUpdateRequests((prev) => prev + 1);
    
    try {
      const response = await updateReceiptQty({ 
        data: { 
          ...data,
          force_submit: force_submit
        } 
      });
      
      // Check if response is a threshold warning (produce or damage)
      if (response && response.status === 'warning' && (response.warning_type === 'threshold_exceeded' || response.warning_type === 'damage_threshold_exceeded')) {
        // Show threshold warning popup
        setThresholdWarningData(response);
        setShowThresholdWarning(true);
        setPendingUpdateData(data);
        setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
        return; // Wait for user confirmation
      }
      
      // Success - no warning
        popup(POPUP_TYPE.SUCCESS, response.message);
        getProductsList(true);
    } catch (error) {
        setActiveUpdateRequests(0);
        setLoading(false);
        popup(POPUP_TYPE.ERR, error.message);
    }
  };

  const handleThresholdWarningConfirm = async (editedValues) => {
    setShowThresholdWarning(false);
    
    // Update quantities if edited
    const finalData = { ...pendingUpdateData };
    if (editedValues && editedValues.length > 0) {
      const editedProduce = editedValues.find(ev => ev.type === 'produce');
      const editedDamage = editedValues.find(ev => ev.type === 'damage');
      if (editedProduce) {
        finalData.done_qty = parseFloat(editedProduce.quantity);
      }
      if (editedDamage) {
        finalData.damage_qty = parseFloat(editedDamage.quantity);
      }
    }
    
    // Re-call API with force_submit=true
    await updateReceipt(finalData, true);
    
    setPendingUpdateData(null);
    setThresholdWarningData(null);
  };

  const handleThresholdWarningCancel = () => {
    setShowThresholdWarning(false);
    setPendingUpdateData(null);
    setThresholdWarningData(null);
    setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
  };

  const handlePrintPackingList = async () => {
    popup(POPUP_TYPE.SUCCESS, 'Print Packing List Initiated.');
    printPackingListDispatch({
      data: { dispatch_id: state?.id },
    })
      .then((response) => {
        setLoading(false);
        if (!response) return;
        if (response.data.length > 0) {
          openProfileDocument(response.data, '_blank', 'noreferrer');
        } else {
          popup(POPUP_TYPE.ERR, response.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        throw err;
      });
  };

  const handleCreateBill = async () => {
    setIsCreateBillLoading(true);
    const dispatch_id = dispatchData?.id;

    try {
      const response = await dispatchBillCreation({
        data: { dispatch_id: dispatch_id, data: [] },
      });

      if (response) {
        popup(POPUP_TYPE.SUCCESS, response?.message || "Bill created successfully.");
        setShowCreateBill(false);
        getReceiptData();
        getProductsList();
      }
    } catch (error) {
      console.error("Error while creating bill:", error);
    } finally {
      setIsCreateBillLoading(false);
    }
  };

  const handleCheckBill = useDebounce(() => {
    setIsCheckBillLoading(true);
    vendorBill({
      data: { bill_ids: [showCheckBill], page_number: 1 },
    })
      .then((response) => {
        setIsCheckBillLoading(false);
        if (response?.data?.records?.length > 0) {
          navigate(`${ROUTES.BILLS}/${response.data.records[0].name.replaceAll('/', '_')}`, {
            state: response.data.records[0],
          });
        }
      })
      .catch(() => setIsCheckBillLoading(false));
  }, 1000);

  const submitDispatch = () => {
    setIsValidateLoading(true);
    markDispatched({
      data: { dispatch_id: [state?.id] },
    })
      .then((response) => {
        setIsValidateLoading(false);
        if (!response) return;
        popup(POPUP_TYPE.SUCCESS, response.message);
        if (state?.dyeing_shipment || state?.partner_id?.vendor_type === 'dyeing') {
          getReceiptData();
          getProductsList();
        }
        // navigate(ROUTES.DISPATCH);
        else window.history.back();
      })
      .catch(() => setIsValidateLoading(false));
  };

  const enableDispatch = () => {
    if (productData?.length) {
      return productData?.every((move) => move?.lotno !== undefined || move.lotno !== false);
    }
  };

  const handleDetailsUpdate = () => {
    setIsUpdateDetailsLoading(true);
    const isInvoiceChanged =
      dispatchData?.invoice_no !== editValues.invoice_no && editValues.invoice_no !== '';
    const isContainerChanged =
      dispatchData?.physical_container_no !== editValues.container_no &&
      editValues.container_no !== '';

    if (!isInvoiceChanged && !isContainerChanged) return;

    vendorUpdateDispatchDetail({
      data: {
        dispatch_id: [state?.id],
        invoice_no: isInvoiceChanged ? editValues?.invoice_no : false,
        physical_container_no: isContainerChanged ? editValues?.container_no : false
      }
    }).then((res) => {
      if (res) {
        popup(POPUP_TYPE.SUCCESS, 'Details updated');
        setIsUpdateDetailsLoading(false);
        getProductsList(true);
        getReceiptData();
      }
    }).catch(() => setIsUpdateDetailsLoading(false)).finally(() => {
      setIsUpdateDetailsLoading(false);
    })
  };

  const isUpdateDetailsDisabled = () => {
    const isInvoiceUnchanged =
      dispatchData?.invoice_no === editValues.invoice_no || !editValues.invoice_no;

    const isContainerUnchanged =
      dispatchData?.physical_container_no === editValues.container_no || !editValues.container_no;

    return (isInvoiceUnchanged && isContainerUnchanged) || isUpdateDetailsLoading;
  };

  const isDispatchButtonDisabled = () => {
    const container = dispatchData?.invoice_no;

    const isInvalidContainer = container === '' || container === false || container === undefined;
    const isDispatchNotEnabled = !enableDispatch();
    const hasNilMoveLines = nilMoveLineCheck(productData);

    return (
      isDispatchNotEnabled ||
      hasNilMoveLines ||
      isInvalidContainer ||
      isValidateLoading
    );
  };



  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.DISPATCH, route: ROUTES.DISPATCH },
          { text: state?.name, route: ROUTES.NULL },
        ]}
      />
      <div className="scroll-div">
        {dispatchData?.dispatch_status === 'draft' && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            {dispatchData?.dispatch_status !== 'dispatched' &&
              dispatchData?.dispatch_status !== 'received' &&
              dispatchData?.state !== 'cancel' && (
                <Button
                  className={`blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${isUpdateDetailsDisabled() ? 'disabled' : ''
                    }`}
                  clickHandler={handleDetailsUpdate}
                  disabled={isUpdateDetailsDisabled()}
                >
                  {isUpdateDetailsLoading && (
                    <span
                      className="spinner-border spinner-border-sm text-white me-2"
                      role="status"
                    />
                  )}
                  {isUpdateDetailsLoading ? 'Updating' : 'Update Details'}
                </Button>
              )}
            {/* Right Section: Dispatch Button */}

            <div className='d-flex'>
              <Button
                className={`blue-btn d-flex align-items-center justify-content-end px-4 mb-3 me-3`}
                clickHandler={handlePrintPackingList}>
                {BUTTON.PRINT_PACKING_LIST}
              </Button>

              <Button
                disabled={isDispatchButtonDisabled()}
                className={`blue-btn d-flex align-items-center justify-content-center px-4 ${isDispatchButtonDisabled() ? 'disabled' : ''
                  }`}
                clickHandler={submitDispatch}
              >
                {isValidateLoading && <span
                  className="spinner-border spinner-border-sm text-white me-2"
                  role="status"
                />}
                {isValidateLoading ? 'Validating...' : 'Validate'}
              </Button>
            </div>
          </div>
        )}

        <div className="d-flex align-items-center justify-content-end">
          {(showCreateBill && getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) !== 'clothing') ? <Button
            className={`blue-btn d-flex align-items-center justify-content-end px-4 mb-3 me-3 ${isCreateBillLoading ? 'disabled' : ''}`}
            disabled={isCreateBillLoading}
            clickHandler={() => {
              setIsCreateBillLoading(true);
              handleCreateBill();
            }}>
            {isCreateBillLoading && (
              <span
                className="spinner-border spinner-border-sm text-white me-2"
                role="status"
              />
            )}
            {isCreateBillLoading ? 'Creating...' : BUTTON.CREATE_BILL}
          </Button> : ''}
          {(showCheckBill && getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) !== 'clothing') ? <Button
            className={`blue-btn d-flex align-items-center justify-content-end px-4 mb-3 me-2 ${isCheckBillLoading ? 'disabled' : ''}`}
            disabled={isCheckBillLoading}
            clickHandler={() => {
              setIsCheckBillLoading(true);
              handleCheckBill();
            }}>
            {isCheckBillLoading && (
              <span
                className="spinner-border spinner-border-sm text-white me-2"
                role="status"
              />
            )}
            {isCheckBillLoading ? 'Checking...' : 'Check Bill'}
          </Button> : ''}
          {dispatchData?.dispatch_status !== 'draft' && <Button
            className={`blue-btn d-flex align-items-center justify-content-end px-4 mb-3 me-3`}
            clickHandler={handlePrintPackingList}>
            {BUTTON.PRINT_PACKING_LIST}
          </Button>}
        </div>
        <DetailBox colCount={2} colData={dispatchData} dataCompiler={dispatchDetailsBox} value={editValues}
          setValue={setEditValues} dispatchData={true} isLoading={isLoading} />
        
        {/* Search Section for Move IDs */}
        <div className="d-flex align-items-center justify-content-start gap-3 w-100 mb-3 mt-3">
          <SearchBar
            search={search}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearch={handleSearch}
            onDefaultSearch={() => {
              setPageNumber(1);
              setSearch(false);
              // Reset search and fetch all data
              setIsMoveIdsLoading(true);
              const params = {
                receipt_id: state.id,
                page_number: 1,
                search: {},
              };
              vendorReceiptMoveIds({ data: params })
                .then((response) => {
                  setIsMoveIdsLoading(false);
                  if (response?.data?.records?.length > 0) {
                    const compiledData = fabricDispatchProductCompiler({ move_ids: response.data.records });
                    setProductData(compiledData);
                    setPaginationData(response.data.params);
                  } else {
                    setProductData([]);
                    setPaginationData({ page: 1, start: 0, end: 0, total_records: 0 });
                  }
                })
                .catch((error) => {
                  setIsMoveIdsLoading(false);
                  popup('error', error.message);
                });
            }}
          />
        </div>
        
        {isUpdateLoading && (
          <span
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
            }}
          >
            <span
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            />
            Updating...
          </span>
        )}
        <PaginatedTable
          columns={filteredColumn}
          initialData={productData}
          paginationData={paginationData}
          setPageNumber={handlePageChange}
          colFilter={false}
          popUp={popUp}
          setPopUp={setPopUp}
          setPopUpData={setPopUpData}
          isLoading={isMoveIdsLoading || isLoading || !dispatchData}
        />
      </div>
      {popUp === 1 && (
        <PopUpBox openPopUpBox={popUp}>
          <QtyUpdate apiCall={updateReceipt} setOpenPopUpBox={setPopUp} data={popUpData} />
        </PopUpBox>
      )}
      {showThresholdWarning && thresholdWarningData && (
        <PopUpBox openPopUpBox={showThresholdWarning} modalWidth="50%">
          <ThresholdWarningPopup
            warningData={thresholdWarningData}
            onConfirm={handleThresholdWarningConfirm}
            onCancel={handleThresholdWarningCancel}
            allowEdit={true}
          />
        </PopUpBox>
      )}
    </>
  );
};

const QtyUpdate = ({ apiCall, setOpenPopUpBox, data }) => {
  const [doneQty, setDoneQty] = useState(data.producing_quantity || '');
  const [damageQty, setDamageQty] = useState(data.damaged_quantity || '');
  const [lotNo, setLotNo] = useState(data.lotno && data.lotno !== 'N/A' ? data.lotno : '');
  const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
  const isYarn = vendorType === 'yarn';
  const qtyRef = useRef();
  const lotRef = useRef();

  const closePopUp = () => {
    setOpenPopUpBox(0);
  };

  const handleApiCall = () => {
    const finalDoneQty = doneQty === '' ? 0 : parseFloat(doneQty);
    const finalDamageQty = damageQty === '' ? 0 : parseFloat(damageQty);
    
    if (!isNaN(finalDoneQty) && finalDoneQty >= 0 && !isNaN(finalDamageQty) && finalDamageQty >= 0) {
      setOpenPopUpBox(0);
      const apiData = {
        move_id: data.move_id,
        done_qty: finalDoneQty,
        damage_qty: finalDamageQty,
      };
      if (lotNo) {
        apiData.lot_name = lotNo;
      }
      apiCall(apiData);
    } else {
      popup(POPUP_TYPE.ERR, POPUP_MSG.INVALID_QTY_MSG);
    }
  };

  const isUpdateDisabled = () => {
    const initialLotNo = data.lotno && data.lotno !== 'N/A' ? data.lotno : '';
    const initialDoneQty = data.producing_quantity || '';
    const initialDamageQty = data.damaged_quantity || '';
    const isLotUnchanged = lotNo === initialLotNo;
    const isDoneQtyUnchanged = doneQty === initialDoneQty;
    const isDamageQtyUnchanged = damageQty === initialDamageQty;
    if (!isYarn) {
      return isDoneQtyUnchanged && isDamageQtyUnchanged;
    }
    return !lotNo || lotNo === 'N/A' || (isLotUnchanged && isDoneQtyUnchanged && isDamageQtyUnchanged);
  }

  useEffect(() => {
    if (lotRef.current) {
      lotRef.current.focus();
    } else {
      qtyRef.current.focus();
    }
  }, []);

  return (
    <>
      <div className="d-flex flex-column m-1 ">
        <h4 className=" text-center text-special mb-3 mt-3"> {isYarn ? POPUP_MSG.ENTER_QTY : 'Enter Quantity'} </h4>
        <hr />
        <div className="w-100 modal-body py-0 d-flex flex-column gap-3 justify-content-center align-items-center">
          {isYarn && (
            <div className="d-flex justify-content-between w-100 ">
              <label className="ms-3">Lot No. <span className="text-danger">*</span></label>
              <input
                type="text"
                className="ps-2 curve-border me-4"
                placeholder={data.lotno || 'Enter lot number'}
                value={lotNo}
                ref={lotRef}
                onChange={(e) => {
                  setLotNo(e.target.value);
                }}
              />
            </div>
          )}
          <div className="d-flex justify-content-between w-100 ">
            <label className="ms-3">Quantity</label>
            <input
              type="number"
              className="ps-2 curve-border me-4"
              value={doneQty}
              ref={qtyRef}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => {
                const value = e.target.value;
                setDoneQty(value);
                if (!value || parseFloat(value) === 0) {
                  setDamageQty('');
                }
              }}
            />
          </div>
          <div className="d-flex justify-content-between w-100 ">
            <label className="ms-3">Dmg Qty.</label>
            <input
              type="number"
              className="ps-2 curve-border me-4"
              value={damageQty}
              disabled={!doneQty || parseFloat(doneQty) === 0}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => {
                setDamageQty(e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      <hr />
      <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4  modal-btn custom-button">
        <div className="ms-5 ps-5 w-50 d-flex align-items-center justify-content-between">
          <Button
            clickHandler={handleApiCall}
            disabled={isUpdateDisabled()}
            className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${isUpdateDisabled() ? 'disabled' : ''}`}>
            {BUTTON.UPDATE}
          </Button>
          <Button
            clickHandler={closePopUp}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      </div>
    </>
  );
};



//   const handleContainerUpdate = useDebounce(() => {
//     dispatch(setLoaderVisibility(true));
//     vendorUpdateContainerNo({
//       data: {
//         dispatch_id: [state?.id],
//         container_id: containerNo,
//       },
//     })
//       .then((response) => {
//         dispatch(setLoaderVisibility(false));
//         popup(POPUP_TYPE.SUCCESS, response.message);
//         setOpenPopUpBox(0);
//         setReload(true);
//       })
//       .catch((error) => {
//         dispatch(setLoaderVisibility(false));
//         popup(POPUP_TYPE.ERR, error.message);
//       });
//   }, 400);

//   const handlePhysicalContainerUpdate = useDebounce(() => {
//     dispatch(setLoaderVisibility(true));
//     vendorUpdatePhysicalContainerNo({
//       data: {
//         dispatch_id: [state?.id],
//         physical_container_no: physicalContainerNo,
//       },
//     })
//       .then((response) => {
//         dispatch(setLoaderVisibility(false));
//         popup(POPUP_TYPE.SUCCESS, response.message);
//         setOpenPopUpBox(0);
//         setReload(true);
//       })
//       .catch((error) => {
//         dispatch(setLoaderVisibility(false));
//         popup(POPUP_TYPE.ERR, error.message);
//       });
//   }, 400);

//   useEffect(() => {
//     containerRef.current.focus();
//   }, []);

//   return (
//     <div className="d-flex flex-column m-1">
//       <h4 className="text-center text-special mb-3 mt-3">Update Dispatch Details</h4>
//       <hr />
//       <div className="w-100 modal-body py-0 d-flex flex-column gap-3 justify-content-center align-items-center">
//         <div className="d-flex justify-content-between w-100">
//           <label className="ms-3">Invoice No</label>
//           <input
//             type="text"
//             className="ps-2 curve-border me-4"
//             placeholder={'Invoice No'}
//             value={containerNo}
//             ref={containerRef}
//             onChange={(e) => setContainerNo(e.target.value)}
//           />
//         </div>
//         <div className="d-flex justify-content-between w-100">
//           <label className="ms-3">Container No</label>
//           <input
//             type="text"
//             className="ps-2 curve-border me-4"
//             placeholder={'Container No'}
//             value={physicalContainerNo}
//             onChange={(e) => setPhysicalContainerNo(e.target.value)}
//           />
//         </div>
//       </div>
//       <hr />
//       <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4 modal-btn custom-button">
//         <Button
//           clickHandler={handleUpdate}
//           className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${(shipmentData.container === containerNo || containerNo === '') &&
//             (shipmentData.physical_container_no === physicalContainerNo ||
//               physicalContainerNo === '')
//             ? 'disabled'
//             : ''
//             }`}
//           disabled={
//             (shipmentData.container === containerNo || containerNo === '') &&
//             (shipmentData.physical_container_no === physicalContainerNo ||
//               physicalContainerNo === '')
//           }>
//           {BUTTON.UPDATE}
//         </Button>
//         <Button
//           clickHandler={() => setOpenPopUpBox(0)}
//           className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
//           {BUTTON.CLOSE}
//         </Button>
//       </div>
//     </div>
//   );
// };
